import { c as _c } from "react/compiler-runtime";
import { useQuery } from "@tanstack/react-query";
import { apiClient } from "@/services/api-client";
import { shouldUseMockData } from "@/lib/mock-data/utils";

/**
 * SIG-340 / SIG-341 — mission-control copy status (contract C1 + C4).
 *
 * Every field mirrors the backend payload verbatim. Position state comes from
 * a live broker read; persisted orders provide attempt history and lag only.
 * Unreadable broker state arrives as `unavailable`, never as a green fill.
 */

/**
 * SIG-379: `skipped` means the copy did NOT happen on purpose — the risk
 * engine declined this follower. It used to be reported as `missed`, so a
 * guard doing its job looked identical to a broker failure, and the genuine
 * misses were buried in the same count.
 *
 * SIG-693: `excluded` means the follower is out of the wave by operator
 * declaration (`copy_exclusion_reason` on its BrokerAccount — e.g. broker
 * Max-Order-Qty-0). Its broker rejects are the predeclared outcome, so it
 * must never render as MISSED nor feed a miss counter.
 */
export type CopyFollowerState = "filled" | "pending" | "missed" | "skipped" | "drifted" | "excluded";
export type BrokerPositionState = "open" | "flat" | "unavailable";
export interface CopyStatusFollower {
  follower_settings_id: number;
  follower_account_id: number;
  account_name: string | null;
  state: CopyFollowerState | null;
  last_copy_at: string | null;
  last_copy_lag_ms: number | null;
  miss_count: number;
  /** SIG-693: predeclared exclusion reason; null for normal followers. */
  excluded_reason: string | null;
  /**
   * SIG-836 r2: the broker's own words for this follower's most recent
   * refusal (persisted `trading_order.reject_reason`, verbatim). Null when
   * the latest copy was not refused, and null for excluded followers — their
   * explanation is `excluded_reason`.
   */
  last_reject_reason: string | null;
  /**
   * Gap-blitz F-03: the refused copy row's own moment, and the moment this
   * follower's sizing row last stamped a NON-refused `pm_sizing` block. When
   * the second is newer than the first the refusal is history — the card ages
   * the red dot out and says so, instead of claiming a copy that never landed.
   * Either can be null (no refusal / no healthy stamp); never inferred.
   */
  last_reject_at?: string | null;
  sizing_healthy_since?: string | null;
  broker_position_state?: BrokerPositionState;
  broker_position_count: number | null;
  /**
   * Classification of the follower's most recent copy row. Exit/flatten legs
   * must not feed the "did not fill" banner (entry-fill expectation).
   */
  latest_leg?: "entry" | "exit" | "flatten" | null;
}
export interface CopyStatusR7Action {
  at: string | null;
  follower_account_id: number;
  account_name: string | null;
  symbol: string | null;
  narration: string;
}
export interface CopyStatusAggregate {
  copies: number;
  avg_lag_ms: number | null;
  max_lag_ms: number | null;
  miss_count: number;
  drift_active: boolean;
  last_r7_action: CopyStatusR7Action | null;

  /**
   * SIG-379 coverage census of the group's configured followers, so a partial
   * fan-out cannot render identically to a complete one. SIG-558 stopped the
   * backend stamping `dispatched` on partial coverage; these are the numbers
   * that tell the user.
   *
   * `followers_missed` is GENUINE failures only — deliberate risk declines are
   * `followers_skipped` and must never be added into it. `followers_idle` had
   * no copy activity in the window: unknown, not "did not fill".
   */
  followers_total: number;
  followers_filled: number;
  followers_pending: number;
  followers_missed: number;
  followers_skipped: number;
  /** SIG-693: predeclared broker exclusions — neither failure nor shortfall. */
  followers_excluded: number;
  followers_idle: number;
}
export interface GroupCopyStatus {
  group_id: number;
  as_of: string;
  window_hours: number;
  followers: CopyStatusFollower[];
  aggregate: CopyStatusAggregate;
  states_unavailable: string[];
}

/** C4 badge format: milliseconds → "+2.1s". Null in → null out (honest "—"). */
export function formatCopyLag(lagMs: number | null | undefined): string | null {
  if (typeof lagMs !== "number" || !Number.isFinite(lagMs)) return null;
  return `+${(lagMs / 1000).toFixed(1)}s`;
}

// C1 liveness v2 (FE-1 item 2): the backend now publishes the COMPLETE
// copy-status snapshot on the unified WS (`copy_status` channel) at copy-wave
// milestones. The 2s poll GATES on those snapshots actually arriving for this
// group: a fresh socket snapshot backs the poll off; silence (no event within
// the coverage window) keeps the 2s band, because the 2–3s copy bar is a
// documented contract and must never rest on a bare poll-slowdown. SIG-363
// still holds: inactive groups do not poll, failures back off, retries are
// spaced.
const COPY_STATUS_POLL_MS = 2_000;
const COPY_STATUS_FAILURE_POLL_MS = 60_000;
// A snapshot this recent proves the socket lane is delivering for this group;
// while covered, the poll relaxes to the gated band. Past the window the poll
// snaps back to 2s on its own.
const COPY_STATUS_SOCKET_COVERAGE_MS = 12_000;
const COPY_STATUS_GATED_POLL_MS = 15_000;

// Last complete-snapshot receipt per group, module-scoped: written by the
// unified WS handler, read by the poll-interval gate.
const lastSnapshotAtByGroup = new Map<number, number>();
export function recordCopyStatusSnapshot(groupId: number, atMs: number = Date.now()): void {
  lastSnapshotAtByGroup.set(groupId, atMs);
}

/** Test seam + logout hygiene. */
export function resetCopyStatusSnapshotReceipts(): void {
  lastSnapshotAtByGroup.clear();
}
function isSocketCovered(groupId: number, nowMs: number): boolean {
  const at = lastSnapshotAtByGroup.get(groupId);
  return typeof at === "number" && nowMs - at < COPY_STATUS_SOCKET_COVERAGE_MS;
}

/**
 * Apply a `copy_status` socket event. Only a COMPLETE snapshot (the
 * GroupCopyStatusResponse shape: group_id + followers + aggregate) is written
 * to the cache and counted as coverage — a partial payload must never slow
 * the poll down.
 */
export function applyCopyStatusSnapshot(queryClient: {
  setQueryData: (key: unknown[], data: unknown) => unknown;
}, payload: unknown, atMs: number = Date.now()): boolean {
  const snapshot = payload as Partial<GroupCopyStatus> | null | undefined;
  if (!snapshot || typeof snapshot.group_id !== "number" || typeof snapshot.as_of !== "string" || !Array.isArray(snapshot.followers) || typeof snapshot.aggregate !== "object" || snapshot.aggregate === null || Array.isArray(snapshot.aggregate) || typeof snapshot.aggregate.followers_total !== "number") {
    return false;
  }
  queryClient.setQueryData(["group-copy-status", snapshot.group_id], snapshot);
  recordCopyStatusSnapshot(snapshot.group_id, atMs);
  return true;
}
export function copyStatusRefetchInterval(fetchFailureCount: number, groupId: number, nowMs: number = Date.now()): number {
  const jitter = groupId % 5 * 500;
  if (fetchFailureCount > 0) {
    return COPY_STATUS_FAILURE_POLL_MS + jitter;
  }
  if (isSocketCovered(groupId, nowMs)) {
    return COPY_STATUS_GATED_POLL_MS + jitter;
  }
  return COPY_STATUS_POLL_MS + jitter;
}
export function copyStatusRetryDelay(attemptIndex: number): number {
  return Math.min(2_000 * 2 ** attemptIndex, 15_000);
}
export function useGroupCopyStatus(groupId, options) {
  const $ = _c(11);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = shouldUseMockData();
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const isMockMode = t0;
  let t1;
  let t2;
  if ($[1] !== groupId) {
    t1 = ["group-copy-status", groupId];
    t2 = async () => {
      const response = await apiClient.get(`/trading-groups/${groupId}/copy-status`);
      return response.data;
    };
    $[1] = groupId;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  const t3 = !isMockMode && Boolean(groupId && groupId > 0) && (options?.enabled ?? true);
  let t4;
  if ($[4] !== groupId) {
    t4 = query => copyStatusRefetchInterval(query.state.fetchFailureCount, groupId ?? 0);
    $[4] = groupId;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== t1 || $[7] !== t2 || $[8] !== t3 || $[9] !== t4) {
    t5 = {
      queryKey: t1,
      queryFn: t2,
      enabled: t3,
      refetchInterval: t4,
      retry: 2,
      retryDelay: copyStatusRetryDelay,
      staleTime: 1000
    };
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  return useQuery(t5);
}